import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { EvFilters, IFilterValues, TFilterOptions } from '@evinced-private/ui-common';

import { useCollections } from '../../../../providers/collections/CollectionsProvider';
import { useCommonFiltersStore } from '../../../../providers/commonFiltersProvider/CommonFiltersProvider';
import { useConfiguration } from '../../../../providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from '../../../../services/LocalTogglesService';
import {
	DASHBOARD_FILTERS_ID,
	defaultFilterValues,
	getFilterDefinitions,
	getInitialFilterValues,
	liftPeriodOptions,
	mapCollectionsToOptions
} from '../../helpers/DashboardFiltersHelper';

import './DashboardFilters.scss';

const filterDefinitions = getFilterDefinitions();

export const DashboardFilters: FC = () => {
	const commonFiltersStore = useCommonFiltersStore();
	const { propertyCollections, isCollectionsLoading } = useCollections();
	const { getToggle } = useConfiguration();

	const isMultipleCollectionsEnabled = getToggle(SiteScannerUiToggles.ENABLE_MULTIPLE_COLLECTIONS);
	const appliedFilters = commonFiltersStore.getFilterValue(DASHBOARD_FILTERS_ID);
	const filterValues = useMemo(
		() => getInitialFilterValues({ appliedFilters, propertyCollections, isCollectionsLoading }),
		[appliedFilters, propertyCollections, isCollectionsLoading]
	);

	const filterOptions: TFilterOptions = useMemo(
		() => ({
			// condition should be removed when multiple collections feature is fully integrated
			collections: isMultipleCollectionsEnabled ? mapCollectionsToOptions(propertyCollections) : [],
			liftPeriods: liftPeriodOptions
		}),
		[propertyCollections, isMultipleCollectionsEnabled]
	);

	const handleApplyFilters = useCallback(
		(appliedFilters: IFilterValues): void => {
			const flatFilters = { ...appliedFilters.select };
			commonFiltersStore.updateSelectedFilters(DASHBOARD_FILTERS_ID, flatFilters);
		},
		[commonFiltersStore]
	);

	const handleResetFilters = useCallback((): void => {
		commonFiltersStore.updateSelectedFilters(DASHBOARD_FILTERS_ID, defaultFilterValues);
	}, [commonFiltersStore]);

	// Aligning the store values with the filter values after removing non-existent collections
	useEffect(() => {
		if (!isCollectionsLoading) {
			commonFiltersStore.updateSelectedFilters(DASHBOARD_FILTERS_ID, { ...filterValues.select });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [commonFiltersStore, isCollectionsLoading]);

	return (
		<div className="dashboard-filters-root">
			<EvFilters
				onApply={handleApplyFilters}
				onReset={handleResetFilters}
				initialValues={filterValues}
				filterOptions={filterOptions}
				filterDefinitions={filterDefinitions}
				isFilterOptionsLoaded={!isCollectionsLoading}
				isInitialValuesLoaded={!isCollectionsLoading}
			/>
		</div>
	);
};
